import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { handleSubmitError } from '@app/forms/form.helper';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@sb/ui/toast';

/**
 * Returns a handler that surfaces a failed hire/rehire submit to the user.
 * Call in an injection context (field initializer or constructor).
 *
 * 422 validation errors that match a form control (e.g. an overlapping
 * contract start date) are mapped onto that control, so the backend message
 * shows inline at the field. Everything else falls back to a danger toast,
 * because the hire requests opt out of the global error toast via
 * IGNORE_META_MESSAGE (see NewHiresService).
 */
export const useHireSubmitErrorHandler = () => {
  const toastService = inject(ToastService);
  const translateService = inject(TranslateService);

  return (submitError: unknown, form: AbstractControl): void => {
    if (submitError instanceof HttpErrorResponse && hasMatchingValidationErrors(submitError, form)) {
      handleSubmitError(submitError, form);
      return;
    }

    const statusMessage =
      submitError instanceof HttpErrorResponse ? submitError.error?.meta?.status_message : undefined;
    toastService.openDanger(statusMessage || translateService.instant('An error has occurred'));
  };
};

const hasMatchingValidationErrors = (submitError: HttpErrorResponse, form: AbstractControl): boolean => {
  if (submitError.status !== 422) {
    return false;
  }

  const validationErrors = submitError.error?.meta?.validation_errors ?? {};
  return Object.keys(validationErrors).some((fieldName) => !!form.get(fieldName));
};

// Maps messages onto controls by dotted path, so nested keys like `children.0.first_name` resolve.
export const applyServerErrors = (form: AbstractControl, errors: Record<string, string>): void => {
  for (const [path, message] of Object.entries(errors)) {
    const control = form.get(path);
    if (!control) continue;

    control.setErrors({ serverSide: message });
    control.markAsTouched();
  }
};

// Scrolls to and focuses the first flagged field inside `host`, so a rejected
// submit reveals the error instead of leaving it out of view.
export const focusFirstInvalidField = (host: HTMLElement): void => {
  const field = host.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (!field) return;

  const prefersReducedMotion =
    host.ownerDocument.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  field.scrollIntoView?.({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center' });
  field.focus({ preventScroll: true });
};
